// scheduling/components/dashboard/PoolView.jsx
// Dashboard "Pool" tab — what happened to the jobs I released (Trello #257).
//
// Sending a job to a pool takes it OUT of My Jobs, which is much of the point:
// small jobs stop cluttering a manager's queue. This tab is where they go, so
// "out of My Jobs" never means "lost track of".
//
// Waiting jobs come first (they still need someone), then recently claimed ones.
// Work status on claimed rows is rprs.Jobs.StatusDetail, kept current by peSync —
// so the manager sees PE's live state without RPRS tracking the job itself.

import { useCallback, useEffect, useState } from 'react';
import { POOL_META } from '../../utils/poolEligibility';
import { JOB_STATUS } from '../../utils/schedConstants';
import { fetchMyPooled } from '../../services/poolApi';
import PoolNotesEditor from '../pool/PoolNotesEditor';

const PE_JOB_URL = (i) => `https://bmss.pehosted.com/PE/Job/Job/${i}?panel=0`;
const fmtHours = (h) => String(Math.round((Number(h) || 0) * 100) / 100);

function waitedLabel(iso) {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  return `${days}d`;
}
function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function PoolView({ navigateToPool }) {
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);   // PoolJobId being edited

  useEffect(() => {
    let alive = true;
    fetchMyPooled(200)
      .then(r => { if (alive) setJobs(r.jobs || []); })
      .catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, []);

  // Patch in place rather than refetch — this list is sorted waiting-then-claimed, and
  // a refetch would rebuild it underneath the row just edited.
  const patchNotes = useCallback((poolJobId, notes) => {
    setJobs(js => (js || []).map(j => (j.PoolJobId === poolJobId ? { ...j, Notes: notes } : j)));
    setEditing(null);
  }, []);

  if (error) {
    return <div className="sched-placeholder"><p style={{ color: '#DC2626' }}>{error}</p></div>;
  }
  if (jobs === null) {
    return <div className="sched-home-loading"><div className="sched-home-loading-text">Loading…</div></div>;
  }
  if (jobs.length === 0) {
    return (
      <div className="sched-pool-tab-empty">
        <div className="sched-pool-empty-icon">◇</div>
        <div className="sched-pool-empty-title">You haven&rsquo;t sent any jobs to a pool</div>
        <div className="sched-pool-empty-sub">
          Use <strong>Send to Pool</strong> on a job in My Jobs to hand a small job to the
          preparer pools instead of scheduling it yourself.
        </div>
      </div>
    );
  }

  const waiting = jobs.filter(j => j.State === 'waiting');
  const claimed = jobs.filter(j => j.State === 'claimed');

  const Row = ({ j }) => {
    const statusStyle = JOB_STATUS[j.StatusDetail] || null;
    // Everything on this tab was pooled BY the caller (my_pooled filters on
    // PooledByStaffId), so the only gate is state: notes stop being editable once the
    // job is claimed, because they were already sent to PE in the forward email.
    const editable = j.State === 'waiting';
    return (
      <tr className="sched-mgr-row">
        <td className="sched-mgr-cell-client">
          <span className="sched-mgr-client-code">{j.ClientCode}</span>
          <span className="sched-mgr-client-name">{j.ClientName}</span>
        </td>
        <td className="sched-mgr-cell-jobname">{j.JobName}</td>
        <td>
          <span className="sched-pool-chip" style={{ background: POOL_META[j.PoolCode]?.color || '#6B7280' }}>
            {POOL_META[j.PoolCode]?.label || j.PoolCode}
          </span>
        </td>
        <td className="sched-mgr-td-right">{fmtHours(j.BudgetHours)}</td>
        <td>
          {j.State === 'waiting'
            ? <span className="sched-pool-wait">Waiting {waitedLabel(j.PooledAt)}</span>
            : (
              <span className="sched-pool-claimed">
                {j.ClaimKind === 'assigned' ? 'Assigned to' : 'Claimed by'}{' '}
                <strong>{j.ClaimedByName}</strong> {fmtDateTime(j.ClaimedAt)}
              </span>
            )}
        </td>
        <td>
          {j.StatusDetail && (
            <span className="sched-badge" style={statusStyle ? { background: statusStyle.bg, color: statusStyle.color } : undefined}>
              {j.StatusDetail}
            </span>
          )}
        </td>
        <td className="sched-pool-tab-notes">
          {editing === j.PoolJobId ? (
            <PoolNotesEditor
              poolJobId={j.PoolJobId}
              notes={j.Notes}
              onSaved={(next) => patchNotes(j.PoolJobId, next)}
              onCancel={() => setEditing(null)}
            />
          ) : j.Notes ? (
            <span className="sched-pool-tab-notes-text" title={j.Notes}>
              {j.Notes}
              {editable && (
                <button type="button" className="sched-pool-notes-editlink"
                  onClick={() => setEditing(j.PoolJobId)}>Edit</button>
              )}
            </span>
          ) : editable ? (
            <button type="button" className="sched-pool-notes-editlink sched-pool-notes-editlink--add"
              onClick={() => setEditing(j.PoolJobId)}>+ Add</button>
          ) : <span className="sched-pool-tab-notes-none">—</span>}
        </td>
        <td>
          {j.PeWriteState === 'failed' && (
            <span className="sched-pool-pefail-badge" title={`PE write failed at step: ${j.PeWriteStep || 'unknown'}. ${j.PeWriteError || ''}`}>
              ⚠ PE
            </span>
          )}
          {j.PEJobIndex && (
            <a className="sched-pool-history-pe" href={PE_JOB_URL(j.PEJobIndex)} target="_blank" rel="noopener noreferrer">PE ↗</a>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="sched-mgr">
      <div className="sched-pool-tab-head">
        <span>
          <strong>{waiting.length}</strong> waiting · <strong>{claimed.length}</strong> claimed
        </span>
        {navigateToPool && (
          <button type="button" className="sched-mgr-pill" onClick={navigateToPool}>
            Open Job Pools →
          </button>
        )}
      </div>

      <div className="sched-mgr-table-wrap">
        <table className="sched-mgr-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Job Name</th>
              <th>Pool</th>
              <th className="sched-mgr-th-right">Budget Hrs</th>
              <th>Status in pool</th>
              <th>PE work status</th>
              <th>Notes</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {waiting.map(j => <Row key={j.PoolJobId} j={j} />)}
            {claimed.length > 0 && waiting.length > 0 && (
              <tr>
                <td colSpan={8} className="sched-pool-tab-divider">Claimed</td>
              </tr>
            )}
            {claimed.map(j => <Row key={j.PoolJobId} j={j} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
